import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import {
  Shield, Users, Search, Key, Trash2, ChevronUp, ChevronDown, Loader2,
  ArrowLeft, LogOut, CheckCircle, AlertTriangle, FileText,
} from 'lucide-react';
import { apiFetch } from '../utils/apiFetch';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO_SRC = process.env.REACT_APP_LOGO_URL;

const fmtDate = (s) => {
  if (!s) return '—';
  try { return new Date(s).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return s; }
};

const AdminPanel = () => {
  const { user, loading, logout } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState('');
  const [fetching, setFetching] = useState(false);
  const [toast, setToast] = useState(null); // {tone:'ok'|'err', text}
  const [confirmDelete, setConfirmDelete] = useState(null); // user obj
  const [resetTarget, setResetTarget] = useState(null);     // user obj
  const [resetPassword, setResetPasswordVal] = useState('CRM2030$$');
  const [busyId, setBusyId] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [showAudit, setShowAudit] = useState(false);

  const flash = (tone, text) => {
    setToast({ tone, text });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchUsers = useCallback(async (search = q) => {
    setFetching(true);
    try {
      const url = new URL(`${API}/admin/users`);
      if (search) url.searchParams.set('q', search);
      url.searchParams.set('limit', '200');
      const [u, s, a] = await Promise.all([
        apiFetch(url.toString()).then((r) => r.json()),
        apiFetch(`${API}/admin/stats`).then((r) => r.json()),
        apiFetch(`${API}/admin/audit-logs?limit=50`).then((r) => r.json()),
      ]);
      setItems(u.items || []);
      setTotal(u.total || 0);
      setStats(s);
      setAuditLogs(a.items || []);
    } catch (e) {
      flash('err', 'Failed to load users');
    } finally {
      setFetching(false);
    }
  }, [q]);

  useEffect(() => {
    if (!loading && user?.role === 'admin') fetchUsers('');
  }, [loading, user, fetchUsers]);

  // ─── Guards ───
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#020817' }}>
        <Loader2 className="w-8 h-8 text-[#00f4ff] animate-spin" />
      </div>
    );
  }
  if (!user) {
    navigate('/myportafolio');
    return null;
  }
  if (user.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center px-6" style={{ backgroundColor: '#020817' }}>
        <Card className="bg-[#040816] border-red-500/30 max-w-md">
          <CardContent className="p-8 text-center">
            <AlertTriangle className="w-12 h-12 text-red-400 mx-auto mb-3" />
            <h2 className="text-xl font-bold text-white mb-2">Admin access only</h2>
            <p className="text-sm text-[#9EB4C0] mb-5">This panel is restricted to administrators.</p>
            <Button onClick={() => navigate('/myportafolio')} className="bg-[#00f4ff] text-[#020817] font-bold rounded-full">
              <ArrowLeft className="w-4 h-4 mr-2" /> Back to My Portafolio
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ─── Actions ───
  const doToggleRole = async (u) => {
    setBusyId(u.user_id);
    try {
      const newRole = u.role === 'admin' ? 'member' : 'admin';
      const r = await apiFetch(`${API}/admin/users/${u.user_id}/role`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ role: newRole }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Failed');
      flash('ok', `${u.email} → ${newRole}`);
      await fetchUsers();
    } catch (e) {
      flash('err', e.message);
    } finally {
      setBusyId(null);
    }
  };

  const doDelete = async () => {
    const u = confirmDelete;
    if (!u) return;
    setBusyId(u.user_id);
    try {
      const r = await apiFetch(`${API}/admin/users/${u.user_id}`, { method: 'DELETE', credentials: 'include' });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Failed');
      flash('ok', `Deleted ${u.email}`);
      setConfirmDelete(null);
      await fetchUsers();
    } catch (e) {
      flash('err', e.message);
    } finally {
      setBusyId(null);
    }
  };

  const doResetPassword = async () => {
    const u = resetTarget;
    if (!u || resetPassword.length < 6) {
      flash('err', 'Password must be ≥ 6 characters');
      return;
    }
    setBusyId(u.user_id);
    try {
      const r = await apiFetch(`${API}/auth/admin/reset-password`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: u.email, new_password: resetPassword }),
      });
      const data = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(data.detail || 'Failed');
      flash('ok', `Password reset for ${u.email}`);
      setResetTarget(null);
    } catch (e) {
      flash('err', e.message);
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="min-h-screen text-white" style={{
      backgroundColor: '#020817',
      backgroundImage: 'radial-gradient(900px 500px at 0% 0%, rgba(124,58,237,0.10), transparent 60%), radial-gradient(700px 400px at 100% 100%, rgba(0,244,255,0.10), transparent 60%)',
    }} data-testid="admin-panel">
      {/* Header */}
      <header className="sticky top-0 z-40 backdrop-blur-md border-b border-[#0b1c38] px-4 lg:px-6 py-3 flex items-center justify-between"
        style={{ background: 'rgba(4,8,22,0.85)' }}>
        <div className="flex items-center gap-3">
          {LOGO_SRC && <img src={LOGO_SRC} alt="DCA" className="h-7 w-auto" />}
          <span className="w-px h-6 bg-white/10" />
          <Shield className="w-5 h-5 text-[#a78bfa]" />
          <span className="text-sm font-black tracking-[0.3em] uppercase" style={{ color: '#a78bfa', textShadow: '0 0 10px rgba(167,139,250,0.5)' }}>
            Admin Panel
          </span>
        </div>
        <div className="flex items-center gap-3">
          <Button onClick={() => navigate('/myportafolio')} variant="outline"
            className="border-[#00f4ff]/40 text-[#00f4ff] hover:bg-[rgba(0,244,255,0.08)] rounded-full text-xs h-9"
            data-testid="admin-back-btn">
            <ArrowLeft className="w-3.5 h-3.5 mr-1.5" /> My Portafolio
          </Button>
          <Button onClick={logout} variant="outline"
            className="border-red-500/40 text-red-400 hover:bg-red-500/10 rounded-full text-xs h-9"
            data-testid="admin-logout-btn">
            <LogOut className="w-3.5 h-3.5 mr-1.5" /> Logout
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 lg:px-6 py-8 space-y-6">
        {/* Welcome */}
        <div>
          <div className="text-[11px] tracking-[0.35em] uppercase text-[#a78bfa]">Control center</div>
          <h1 className="text-3xl font-black mt-1">Welcome, {user.name}</h1>
          <p className="text-sm text-[#9EB4C0] mt-1">Manage members, roles and passwords.</p>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="admin-stats">
            <StatCard label="Users"          value={stats.users}         color="#00f4ff" />
            <StatCard label="Admins"         value={stats.admins}        color="#a78bfa" />
            <StatCard label="MT Accounts"    value={stats.mt_accounts}   color="#22c55e" />
            <StatCard label="Wallets"        value={stats.wallets}       color="#12b7ff" />
            <StatCard label="DCA Purchases"  value={stats.dca_purchases} color="#f7931a" />
          </div>
        )}

        {/* Search */}
        <Card className="bg-[#040816] border-[#0b1c38]">
          <CardContent className="p-4 flex items-center gap-3">
            <Search className="w-4 h-4 text-[#9EB4C0]" />
            <Input
              placeholder="Search by email…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') fetchUsers(q); }}
              className="bg-[#0b1c38] border-[#0b1c38] text-white h-9 flex-1"
              data-testid="admin-search-input"
            />
            <Button onClick={() => fetchUsers(q)} disabled={fetching}
              className="bg-[#00f4ff] text-[#020817] font-bold rounded-full px-4 h-9"
              data-testid="admin-search-btn">
              {fetching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
            </Button>
            <span className="text-[11px] text-[#556475] uppercase tracking-[0.25em]">
              {items.length}/{total}
            </span>
          </CardContent>
        </Card>

        {/* Users table */}
        <Card className="bg-[#040816] border-[#0b1c38] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="admin-users-table">
              <thead>
                <tr className="text-[10px] uppercase tracking-[0.25em] text-[#556475] border-b border-[#0b1c38]">
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3">Name</th>
                  <th className="text-left px-4 py-3">Role</th>
                  <th className="text-left px-4 py-3">Created</th>
                  <th className="text-right px-4 py-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((u) => {
                  const isMe = u.user_id === user.user_id;
                  const isAdmin = u.role === 'admin';
                  return (
                    <tr key={u.user_id} className="border-b border-[#0b1c38] hover:bg-white/[0.02]"
                      data-testid={`user-row-${u.email}`}>
                      <td className="px-4 py-3 font-mono text-[12px] text-white">
                        {u.email}
                        {isMe && <span className="ml-2 text-[9px] uppercase tracking-[0.25em] text-[#00f4ff]">You</span>}
                      </td>
                      <td className="px-4 py-3 text-[#cfe1f0]">{u.name || '—'}</td>
                      <td className="px-4 py-3">
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.25em]"
                          style={{
                            background: isAdmin ? 'rgba(167,139,250,0.15)' : 'rgba(0,244,255,0.10)',
                            color:      isAdmin ? '#a78bfa' : '#00f4ff',
                            border: `1px solid ${isAdmin ? 'rgba(167,139,250,0.35)' : 'rgba(0,244,255,0.30)'}`,
                          }}>
                          <Shield className="w-2.5 h-2.5" /> {u.role || 'member'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-[11px] text-[#9EB4C0]">{fmtDate(u.created_at)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            onClick={() => { setResetTarget(u); setResetPasswordVal('CRM2030$$'); }}
                            disabled={busyId === u.user_id}
                            className="bg-[#00f4ff]/10 hover:bg-[#00f4ff]/20 text-[#00f4ff] border border-[#00f4ff]/30 rounded-full h-7 text-[11px] px-3"
                            data-testid={`reset-pwd-${u.email}`}>
                            <Key className="w-3 h-3 mr-1" /> Reset PWD
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => doToggleRole(u)}
                            disabled={busyId === u.user_id || isMe}
                            className="bg-[#a78bfa]/10 hover:bg-[#a78bfa]/20 text-[#a78bfa] border border-[#a78bfa]/30 rounded-full h-7 text-[11px] px-3 disabled:opacity-30"
                            data-testid={`toggle-role-${u.email}`}>
                            {isAdmin
                              ? <><ChevronDown className="w-3 h-3 mr-1" /> Demote</>
                              : <><ChevronUp className="w-3 h-3 mr-1" /> Promote</>}
                          </Button>
                          <Button
                            size="sm"
                            onClick={() => setConfirmDelete(u)}
                            disabled={busyId === u.user_id || isMe}
                            className="bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/30 rounded-full h-7 text-[11px] px-3 disabled:opacity-30"
                            data-testid={`delete-${u.email}`}>
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                {items.length === 0 && !fetching && (
                  <tr><td colSpan="5" className="text-center py-12 text-[#556475]">No users found</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Audit Log */}
        <Card className="bg-[#040816] border-[#a78bfa]/25 overflow-hidden">
          <button
            onClick={() => setShowAudit(!showAudit)}
            className="w-full px-5 py-3 flex items-center justify-between hover:bg-white/[0.02] transition"
            data-testid="audit-toggle"
          >
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-[#a78bfa]" />
              <h3 className="text-base font-bold text-white">Audit Log</h3>
              <span className="text-[10px] tracking-[0.25em] uppercase text-[#a78bfa]">
                {auditLogs.length} entries
              </span>
            </div>
            {showAudit ? <ChevronUp className="w-4 h-4 text-[#9EB4C0]" /> : <ChevronDown className="w-4 h-4 text-[#9EB4C0]" />}
          </button>
          {showAudit && (
            <div className="border-t border-[#0b1c38] overflow-x-auto" data-testid="audit-log-list">
              {auditLogs.length === 0 ? (
                <div className="text-center py-10 text-[#556475] text-sm">No admin actions logged yet.</div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[10px] uppercase tracking-[0.25em] text-[#556475] border-b border-[#0b1c38]">
                      <th className="text-left px-4 py-3">When</th>
                      <th className="text-left px-4 py-3">Actor</th>
                      <th className="text-left px-4 py-3">Action</th>
                      <th className="text-left px-4 py-3">Target</th>
                      <th className="text-left px-4 py-3">Details</th>
                    </tr>
                  </thead>
                  <tbody>
                    {auditLogs.map((log) => {
                      const tone = log.action === 'user_delete' ? '#ef4444'
                                 : log.action === 'role_change' ? '#a78bfa'
                                 : '#00f4ff';
                      return (
                        <tr key={log.log_id} className="border-b border-[#0b1c38] hover:bg-white/[0.02]">
                          <td className="px-4 py-2 text-[11px] text-[#9EB4C0]">{fmtDate(log.created_at)}</td>
                          <td className="px-4 py-2 font-mono text-[11px] text-white">{log.actor_email}</td>
                          <td className="px-4 py-2">
                            <span className="inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.2em]"
                              style={{
                                background: `${tone}18`,
                                color: tone,
                                border: `1px solid ${tone}40`,
                              }}>
                              {log.action.replace('_', ' ')}
                            </span>
                          </td>
                          <td className="px-4 py-2 font-mono text-[11px] text-[#cfe1f0]">{log.target_email || '—'}</td>
                          <td className="px-4 py-2 text-[10px] text-[#9EB4C0] font-mono">
                            {log.details && Object.keys(log.details).length > 0
                              ? Object.entries(log.details).map(([k, v]) => `${k}=${v}`).join(' · ')
                              : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          )}
        </Card>

        <p className="text-[10px] text-[#556475] text-center tracking-[0.25em] uppercase">
          Admin Panel · The DCA Club · Sim360°
        </p>
      </main>

      {/* Reset password modal */}
      {resetTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/70 backdrop-blur-sm" data-testid="reset-pwd-modal">
          <Card className="bg-[#040816] border-[#00f4ff]/40 max-w-md w-full">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-4">
                <Key className="w-5 h-5 text-[#00f4ff]" />
                <h3 className="text-lg font-bold text-white">Reset password</h3>
              </div>
              <p className="text-sm text-[#9EB4C0] mb-4">
                User: <span className="font-mono text-white">{resetTarget.email}</span>
              </p>
              <Label className="text-[11px] text-[#9EB4C0]">New password</Label>
              <Input
                type="text"
                value={resetPassword}
                onChange={(e) => setResetPasswordVal(e.target.value)}
                className="bg-[#0b1c38] border-[#0b1c38] text-white h-10 mt-1 font-mono"
                data-testid="reset-pwd-input"
              />
              <p className="text-[10px] text-[#556475] mt-2">
                Tell the user this temporary password. They can change it from "Settings" once logged in.
              </p>
              <div className="flex gap-2 mt-5">
                <Button onClick={() => setResetTarget(null)} variant="outline"
                  className="flex-1 border-white/10 text-white hover:bg-white/5 rounded-full h-10">
                  Cancel
                </Button>
                <Button onClick={doResetPassword} disabled={busyId === resetTarget.user_id || resetPassword.length < 6}
                  className="flex-1 bg-[#00f4ff] text-[#020817] font-bold rounded-full h-10"
                  data-testid="confirm-reset-pwd">
                  {busyId === resetTarget.user_id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reset password'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Delete confirmation modal */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center px-4 bg-black/70 backdrop-blur-sm" data-testid="delete-modal">
          <Card className="bg-[#040816] border-red-500/40 max-w-md w-full">
            <CardContent className="p-6">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                <h3 className="text-lg font-bold text-white">Delete user?</h3>
              </div>
              <p className="text-sm text-[#9EB4C0]">
                This will permanently delete <span className="font-mono text-white">{confirmDelete.email}</span> and all their data (DCA, wallets, MT accounts, sessions). This cannot be undone.
              </p>
              <div className="flex gap-2 mt-5">
                <Button onClick={() => setConfirmDelete(null)} variant="outline"
                  className="flex-1 border-white/10 text-white hover:bg-white/5 rounded-full h-10">
                  Cancel
                </Button>
                <Button onClick={doDelete} disabled={busyId === confirmDelete.user_id}
                  className="flex-1 bg-red-500 hover:bg-red-600 text-white font-bold rounded-full h-10"
                  data-testid="confirm-delete-btn">
                  {busyId === confirmDelete.user_id ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Delete forever'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div
          className="fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg text-sm shadow-2xl flex items-center gap-2 animate-in fade-in slide-in-from-bottom-2"
          style={{
            background: toast.tone === 'ok' ? 'rgba(34,197,94,0.95)' : 'rgba(239,68,68,0.95)',
            color: '#020817',
          }}
          data-testid="admin-toast"
        >
          {toast.tone === 'ok' ? <CheckCircle className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />}
          {toast.text}
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, color }) => (
  <Card className="bg-[#040816]" style={{ border: `1px solid ${color}33`, boxShadow: `0 0 18px -8px ${color}66` }}>
    <CardContent className="p-3">
      <div className="text-[9px] tracking-[0.3em] uppercase text-[#556475]">{label}</div>
      <div className="text-2xl font-black tabular-nums mt-1" style={{ color, textShadow: `0 0 12px ${color}55`, fontFamily: 'ui-monospace, monospace' }}>
        {value}
      </div>
    </CardContent>
  </Card>
);

export default AdminPanel;
